
import { useMemo, useState } from "react";
import { useAuthStore } from "@/store/auth";
import { useUiStore } from "@/store/ui";
import { api } from "@/lib/api";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { LoadingState, EmptyState, PageHeader, ErrorState } from "@/components/features/states";
import { JobStatusBadge } from "@/components/features/badges";
import { formatPrice, formatRelativeTime, currencySymbol } from "@/lib/format";
import type { DeliveryJob, JobStatus, JobVertical } from "@/lib/types";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  Search,
  Package,
  Truck,
  MapPin,
  Navigation,
  Clock,
  CheckCircle2,
  Circle,
  CircleDot,
  MessageSquare,
  Phone,
  Star,
  ChevronRight,
  Utensils,
  ShoppingCart,
  Pill,
  Droplets,
  FileText,
  Bike,
  ListChecks,
  PackageCheck,
  Hash,
  AlertCircle,
} from "lucide-react";

// ============================================================
// Constants
// ============================================================

/** Active job statuses — live deliveries being fulfilled right now. */
const ACTIVE_STATUSES: JobStatus[] = ["open", "assigned", "picked_up", "in_transit"];

/** Past / terminal job statuses. */
const PAST_STATUSES: JobStatus[] = ["delivered", "completed", "cancelled", "disputed"];

/** The 6-step tracking timeline (pickup_deliver / errand mode). */
const TIMELINE_PICKUP: { key: JobStatus; label: string; icon: typeof Package }[] = [
  { key: "open", label: "track.step.posted", icon: Hash },
  { key: "assigned", label: "track.step.assigned", icon: CircleDot },
  { key: "picked_up", label: "track.step.pickedUp", icon: PackageCheck },
  { key: "in_transit", label: "track.step.inTransit", icon: Truck },
  { key: "delivered", label: "track.step.delivered", icon: CheckCircle2 },
  { key: "completed", label: "track.step.completed", icon: Star },
];

/** Order & Deliver timeline (merchant orders: preparing → out for delivery). */
const TIMELINE_ORDER: { key: string; label: string; icon: typeof Package }[] = [
  { key: "open", label: "track.step.ordered", icon: Hash },
  { key: "assigned", label: "track.step.accepted", icon: CircleDot },
  { key: "preparing", label: "track.step.preparing", icon: ListChecks },
  { key: "picked_up", label: "track.step.pickedUp", icon: PackageCheck },
  { key: "out_for_delivery", label: "track.step.outForDelivery", icon: Truck },
  { key: "delivered", label: "track.step.delivered", icon: CheckCircle2 },
  { key: "completed", label: "track.step.completed", icon: Star },
];

/** Vertical icon map. */
const VERTICAL_ICON: Record<JobVertical, typeof Package> = {
  parcel: Package,
  food: Utensils,
  grocery: ShoppingCart,
  pharmacy: Pill,
  water: Droplets,
  courier: FileText,
  errand: Bike,
  other: Package,
};

/** Determine the current timeline step index for a job. */
function currentStepIndex(job: DeliveryJob, isOrderMode: boolean): number {
  const timeline = isOrderMode ? TIMELINE_ORDER : TIMELINE_PICKUP;
  if (isOrderMode) {
    // Order & Deliver uses preparing/out_for_delivery intermediate steps.
    if (job.completedAt) return 6;
    if (job.deliveredAt || job.status === "delivered") return 5;
    if (job.outForDeliveryAt || job.status === "in_transit") return 4;
    if (job.pickedUpAt || job.status === "picked_up") return 3;
    if (job.preparingAt) return 2;
    if (job.assignedAt || job.status === "assigned") return 1;
    return 0;
  }
  // Pickup & Deliver standard timeline.
  const order: JobStatus[] = ["open", "assigned", "picked_up", "in_transit", "delivered", "completed"];
  let idx = order.indexOf(job.status);
  if (idx < 0) {
    // cancelled / disputed → show up to last reached step
    if (job.deliveredAt) idx = 4;
    else if (job.pickedUpAt) idx = 3;
    else if (job.assignedAt) idx = 1;
    else idx = 0;
  }
  return idx;
}

/** Estimate ETA (minutes) for an in-transit job based on distance + assumed speed. */
function estimateEtaMinutes(job: DeliveryJob): number | null {
  if (job.status !== "in_transit" && job.status !== "picked_up") return null;
  const km = job.quotedDistanceKm;
  if (!km || km <= 0) return null;
  // Assume average urban speed of 25 km/h → ~2.4 min/km, min 5 min.
  const mins = Math.max(5, Math.round((km / 25) * 60));
  return mins;
}

// ============================================================
// Main TrackView
// ============================================================

export function TrackView() {
  const { user } = useAuthStore();
  const navigate = useUiStore((s) => s.navigate);
  const { t } = useTranslation();
  const [tab, setTab] = useState<"active" | "past">("active");
  const [query, setQuery] = useState("");

  // Determine the role param for the API based on the user's active role.
  // customer → "customer"; driver/provider → "provider"; admin → fetch all.
  const roleParam = user?.isAdmin
    ? undefined
    : user?.activeRole === "customer"
    ? "customer"
    : "provider";

  // Fetch active jobs.
  const activeQuery = useQuery({
    queryKey: ["track-jobs", "active", roleParam],
    queryFn: async () => {
      // Fetch both active and past in one go (the API doesn't filter by status
      // array, so we fetch without status and split client-side).
      const res = await api.jobs.list(roleParam ? { role: roleParam } : undefined);
      return res.ok ? res.data!.jobs : [];
    },
    staleTime: 15_000, // refresh every 15s for live tracking
    refetchInterval: 15_000,
  });

  const jobs = activeQuery.data || [];

  // Split into active vs past.
  const { activeJobs, pastJobs } = useMemo(() => {
    const active = jobs.filter((j) => ACTIVE_STATUSES.includes(j.status));
    const past = jobs.filter((j) => PAST_STATUSES.includes(j.status));
    // Sort: active by most recent update, past by most recent first.
    active.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    past.sort((a, b) => new Date(b.completedAt || b.updatedAt).getTime() - new Date(a.completedAt || a.updatedAt).getTime());
    return { activeJobs: active, pastJobs: past };
  }, [jobs]);

  // Apply search filter.
  const filterByQuery = (list: DeliveryJob[]) => {
    const q = query.trim().toLowerCase();
    if (!q) return list;
    return list.filter(
      (j) =>
        j.code.toLowerCase().includes(q) ||
        j.pickupAddress.toLowerCase().includes(q) ||
        j.dropoffAddress.toLowerCase().includes(q) ||
        j.parcelDescription.toLowerCase().includes(q) ||
        (j.store?.name || "").toLowerCase().includes(q)
    );
  };

  const displayList = filterByQuery(tab === "active" ? activeJobs : pastJobs);

  if (activeQuery.isLoading) {
    return (
      <div className="mx-auto max-w-2xl">
        <PageHeader title={t("track.title")} subtitle={t("track.subtitle")} />
        <LoadingState label={t("track.loading")} />
      </div>
    );
  }

  if (activeQuery.isError) {
    return (
      <div className="mx-auto max-w-2xl">
        <PageHeader title={t("track.title")} subtitle={t("track.subtitle")} />
        <ErrorState
          message={t("track.error")}
          onRetry={() => activeQuery.refetch()}
        />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <PageHeader title={t("track.title")} subtitle={t("track.subtitle")} />

      {/* Search bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder={t("track.searchPlaceholder")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label={t("track.searchPlaceholder")}
              />
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {t("track.searchHint")}
          </p>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex gap-1 rounded-lg bg-muted p-1">
        <button
          onClick={() => setTab("active")}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-sm font-semibold transition ${
            tab === "active" ? "bg-background shadow text-foreground" : "text-muted-foreground"
          }`}
        >
          <Navigation className="h-4 w-4" />
          {t("track.tabActive")}
          {activeJobs.length > 0 && (
            <span className="ml-0.5 rounded-full bg-primary/15 px-1.5 text-xs font-bold text-primary">
              {activeJobs.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab("past")}
          className={`flex flex-1 items-center justify-center gap-1.5 rounded-md py-2 text-sm font-semibold transition ${
            tab === "past" ? "bg-background shadow text-foreground" : "text-muted-foreground"
          }`}
        >
          <Clock className="h-4 w-4" />
          {t("track.tabPast")}
          {pastJobs.length > 0 && (
            <span className="ml-0.5 rounded-full bg-muted-foreground/15 px-1.5 text-xs font-bold text-muted-foreground">
              {pastJobs.length}
            </span>
          )}
        </button>
      </div>

      {/* Results */}
      {displayList.length === 0 ? (
        <EmptyState
          icon={tab === "active" ? <Navigation className="h-8 w-8" /> : <Clock className="h-8 w-8" />}
          title={query ? t("track.noResults") : tab === "active" ? t("track.noActive") : t("track.noPast")}
          description={query ? t("track.tryDifferentSearch") : tab === "active" ? t("track.noActiveDesc") : t("track.noPastDesc")}
          action={
            tab === "active" && !query ? (
              <Button onClick={() => navigate("app/jobs/new")} className="gap-1.5">
                <Package className="h-4 w-4" /> {t("track.newDelivery")}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-4">
          {displayList.map((job) => (
            <TrackCard key={job.id} job={job} />
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// TrackCard — rich tracking card for a single delivery
// ============================================================

function TrackCard({ job }: { job: DeliveryJob }) {
  const navigate = useUiStore((s) => s.navigate);
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const isOrderMode = job.mode === "order_deliver";
  const timeline = isOrderMode ? TIMELINE_ORDER : TIMELINE_PICKUP;
  const currentIdx = currentStepIndex(job, isOrderMode);
  const isCancelled = job.status === "cancelled";
  const isDisputed = job.status === "disputed";

  const VIcon = VERTICAL_ICON[job.parcelCategory] || Package;
  const eta = estimateEtaMinutes(job);
  const driver = job.provider;
  const isMyJobAsCustomer = user?.id === job.customerId;
  const isMyJobAsDriver = user?.id === job.providerId;

  // Payment summary
  const price = job.agreedPrice ?? job.customerOffer;
  const ccy = currencySymbol(user?.countryCode || "+253");

  return (
    <Card className={isCancelled ? "opacity-75" : ""}>
      <CardContent className="space-y-4 p-4">
        {/* Header row: code + status + vertical */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <VIcon className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-mono text-sm font-bold tracking-wide">{job.code}</span>
                {isOrderMode && job.store && (
                  <span className="truncate text-xs text-muted-foreground">
                    · {job.store.name}
                  </span>
                )}
              </div>
              <p className="truncate text-xs text-muted-foreground">
                {isOrderMode ? t("track.orderDeliver") : t("track.pickupDeliver")} ·{" "}
                {formatRelativeTime(job.createdAt)}
              </p>
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            <JobStatusBadge status={job.status} />
            <span className="text-xs font-medium text-muted-foreground">
              {formatPrice(price, user?.countryCode)}
            </span>
          </div>
        </div>

        {/* Dispute / cancel banner */}
        {isCancelled && (
          <div className="flex items-center gap-2 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>
              {job.cancelReason
                ? t("track.cancelledWithReason", { reason: job.cancelReason })
                : t("track.cancelled")}
            </span>
          </div>
        )}
        {isDisputed && (
          <div className="flex items-center gap-2 rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-700 dark:text-amber-400">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span>{t("track.disputed")}</span>
          </div>
        )}

        {/* Timeline stepper */}
        {!isCancelled && !isDisputed && (
          <TimelineStepper
            steps={timeline}
            currentIdx={currentIdx}
            isOrderMode={isOrderMode}
          />
        )}

        {/* Route: pickup → dropoff */}
        <div className="space-y-2 rounded-lg border border-border/60 bg-muted/30 p-3">
          <RouteRow
            icon={Circle}
            label={t("track.pickup")}
            address={job.pickupAddress}
            time={job.pickedUpAt || job.assignedAt}
            color="text-primary"
          />
          <div className="ml-[7px] h-4 border-l-2 border-dashed border-border" />
          <RouteRow
            icon={MapPin}
            label={t("track.dropoff")}
            address={job.dropoffAddress}
            time={job.deliveredAt}
            color="text-amber-600"
          />
        </div>

        {/* ETA banner for in-transit */}
        {eta && job.status === "in_transit" && (
          <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
            <motion.div
              animate={{ scale: [1, 1.15, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <Navigation className="h-4 w-4 text-primary" />
            </motion.div>
            <span className="text-sm font-semibold text-primary">
              {t("track.onTheWay")}
            </span>
            <div className="ml-auto flex items-center gap-1.5 text-sm">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="font-semibold">{t("track.eta", { mins: eta })}</span>
              <span className="text-muted-foreground">{t("track.etaLabel")}</span>
            </div>
            {job.quotedDistanceKm && (
              <span className="ml-2 text-xs text-muted-foreground">
                ~{job.quotedDistanceKm.toFixed(1)} km
              </span>
            )}
          </div>
        )}

        {/* Driver / courier info */}
        {driver && job.status !== "open" && (
          <div className="flex items-center gap-3 rounded-lg border border-border/60 p-3">
            <Avatar className="h-10 w-10">
              <AvatarFallback className="bg-primary/10 text-sm font-bold text-primary">
                {driver.name.slice(0, 2).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold">
                {isMyJobAsDriver ? t("track.youDriving") : driver.name}
              </p>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-0.5">
                  <Star className="h-3 w-3 fill-amber-400 text-amber-400" />
                  {driver.rating.toFixed(1)}
                </span>
                <span>·</span>
                <span className="capitalize">{job.vehicleRequired}</span>
                <span>·</span>
                <span>{t("track.jobsCount", { count: driver.ratingCount ?? 0 })}</span>
              </div>
            </div>
            {isMyJobAsCustomer && (
              <div className="flex gap-1.5">
                <Button
                  size="icon"
                  variant="outline"
                  className="h-8 w-8"
                  onClick={() => navigate(`app/chat/${job.id}`)}
                  aria-label={t("track.chat")}
                >
                  <MessageSquare className="h-4 w-4" />
                </Button>
                {driver.phone && (
                  <a href={`tel:${driver.phone}`} aria-label={t("track.call")}>
                    <Button size="icon" variant="outline" className="h-8 w-8">
                      <Phone className="h-4 w-4" />
                    </Button>
                  </a>
                )}
              </div>
            )}
          </div>
        )}

        {/* Confirmation code (cash on delivery) */}
        {isMyJobAsCustomer && job.confirmationCode && job.status !== "completed" && job.status !== "delivered" && (
          <div className="flex items-center justify-between rounded-lg border border-amber-500/30 bg-amber-500/5 px-3 py-2">
            <div>
              <p className="text-xs font-medium text-amber-700 dark:text-amber-400">
                {t("track.confirmationCode")}
              </p>
              <p className="text-xs text-muted-foreground">{t("track.codeHint")}</p>
            </div>
            <span className="font-mono text-lg font-bold tracking-[0.3em] text-amber-700 dark:text-amber-400">
              {job.confirmationCode}
            </span>
          </div>
        )}

        {/* Footer actions */}
        <div className="flex items-center justify-between border-t border-border/60 pt-3">
          <span className="text-xs text-muted-foreground">
            {t("track.updated")} {formatRelativeTime(job.updatedAt)}
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1 text-primary"
            onClick={() => navigate(`app/jobs/${job.id}`)}
          >
            {t("track.viewDetails")}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================
// TimelineStepper — horizontal progress stepper
// ============================================================

function TimelineStepper({
  steps,
  currentIdx,
  isOrderMode,
}: {
  steps: { key: string; label: string; icon: typeof Package }[];
  currentIdx: number;
  isOrderMode: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div className="px-1">
      <div className="flex items-start justify-between">
        {steps.map((step, i) => {
          const isDone = i < currentIdx;
          const isCurrent = i === currentIdx;
          const isPending = i > currentIdx;
          const StepIcon = step.icon;
          return (
            <div
              key={step.key}
              className="flex flex-1 flex-col items-center"
            >
              {/* Connector line (left side, except first) */}
              <div className="relative flex w-full items-center justify-center">
                {i > 0 && (
                  <div
                    className={`absolute right-1/2 top-1/2 h-0.5 w-full -translate-y-1/2 ${
                      i <= currentIdx ? "bg-primary" : "bg-border"
                    }`}
                  />
                )}
                <motion.div
                  initial={false}
                  animate={{
                    scale: isCurrent ? [1, 1.12, 1] : 1,
                  }}
                  transition={{
                    duration: 1.8,
                    repeat: isCurrent ? Infinity : 0,
                  }}
                  className={`relative z-10 flex h-7 w-7 items-center justify-center rounded-full border-2 transition-colors ${
                    isDone
                      ? "border-primary bg-primary text-primary-foreground"
                      : isCurrent
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border bg-background text-muted-foreground"
                  }`}
                >
                  {isDone ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : isCurrent ? (
                    <StepIcon className="h-3.5 w-3.5" />
                  ) : (
                    <Circle className="h-3 w-3 text-muted-foreground/40" />
                  )}
                </motion.div>
              </div>
              {/* Label */}
              <span
                className={`mt-1.5 text-center text-[10px] font-medium leading-tight ${
                  isDone
                    ? "text-foreground"
                    : isCurrent
                    ? "text-primary font-semibold"
                    : "text-muted-foreground"
                }`}
              >
                {t(step.label)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================
// RouteRow — pickup/dropoff address row
// ============================================================

function RouteRow({
  icon: Icon,
  label,
  address,
  time,
  color,
}: {
  icon: typeof MapPin;
  label: string;
  address: string;
  time: string | null;
  color: string;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${color}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {label}
          </span>
          {time && (
            <span className="shrink-0 text-xs text-muted-foreground">
              {formatRelativeTime(time)}
            </span>
          )}
        </div>
        <p className="truncate text-sm font-medium text-foreground">{address}</p>
      </div>
    </div>
  );
}
